import type { PlanFeature, PlanTierOffer } from '../../types/planTiers';
import { formatPrice, threeYearPriceLine } from '../signup/signupFormat';
import { PricingCta } from './PricingCta';
import { Tick } from './PricingTierCard';

/** A dash for a feature the plan leaves out. Decoration: the visible "Not included" carries it. */
const Dash = () => (
  <svg
    className="pricing-plan__dash"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M6 12h12" />
  </svg>
);

/**
 * Every plan feature, marked included or not for this plan. Included reads "Player Database,
 * included" to a screen reader; left out shows a visible "Not included", so neither depends on
 * colour or the icon alone. `allFeatures` empty (should not happen with plans on sale) falls back
 * to listing what the plan has.
 */
const PlanFeatures = ({ plan, allFeatures }: { plan: PlanTierOffer; allFeatures: PlanFeature[] }) => {
  const included = new Set(plan.features.map((feature) => feature.key));
  const rows = allFeatures.length > 0 ? allFeatures : plan.features;
  return (
    <ul className="pricing-card__facts pricing-plan__features" aria-label={`What ${plan.name} includes`}>
      <li>
        <Tick />
        <span>Everything in the core app</span>
      </li>
      {rows.map((feature) =>
        included.has(feature.key) ? (
          <li key={feature.key} data-testid={`pricing-plan-${plan.key}-${feature.key}`}>
            <Tick />
            <span>
              {feature.name}
              <span className="pricing-visually-hidden">, included</span>
            </span>
          </li>
        ) : (
          <li
            key={feature.key}
            className="pricing-plan__feature--off"
            data-testid={`pricing-plan-${plan.key}-${feature.key}`}
          >
            <Dash />
            <span>
              {feature.name}
              <span className="pricing-plan__off-note">
                <span className="pricing-visually-hidden">, </span>Not included
              </span>
            </span>
          </li>
        )
      )}
    </ul>
  );
};

/** One baseball plan: name, yearly price, who it suits, what it includes and leaves out, the way in. */
export const PricingPlanCard = ({
  plan,
  allFeatures,
  currency,
  threeYearOfferEndsOn = null,
  signupAvailable,
}: {
  plan: PlanTierOffer;
  allFeatures: PlanFeature[];
  currency: string;
  /** The last day (yyyy-MM-dd) to sign a 3-year agreement, added to the 3-year line. */
  threeYearOfferEndsOn?: string | null;
  signupAvailable: boolean;
}) => {
  const threeYearLine = threeYearPriceLine(plan.threeYearAnnualPriceCents, currency, threeYearOfferEndsOn);
  return (
    <article
      className="pricing-card"
      aria-labelledby={`pricing-plan-${plan.key}`}
      data-testid={`pricing-plan-${plan.key}`}
    >
      <h4 className="pricing-card__name" id={`pricing-plan-${plan.key}`}>
        {plan.name}
      </h4>
      {plan.description && <p className="pricing-card__for">{plan.description}</p>}
      <p className="pricing-card__price" data-testid={`pricing-plan-price-${plan.key}`}>
        <span className="pricing-card__amount">{formatPrice(plan.annualPriceCents, currency)}</span>{' '}
        <span className="pricing-card__per">/ year</span>
      </p>
      {threeYearLine && (
        <p className="pricing-card__for" data-testid={`pricing-plan-three-year-${plan.key}`}>
          {threeYearLine}
        </p>
      )}
      <PlanFeatures plan={plan} allFeatures={allFeatures} />
      <div className="pricing-card__action">
        <PricingCta
          signupAvailable={signupAvailable}
          tierName={plan.name}
          block
          testId={`pricing-plan-cta-${plan.key}`}
        />
      </div>
    </article>
  );
};
